export interface Usuario {
  usuarioId: number;
  telefono: string;
  nombreUsuario: string;
  activo: boolean;
  fechaCreacion: string;
  lastLogin: string | null;
  imagenUrl: string | null;
  empresaId: number | null;
}

export interface CreateUsuarioDto {
  telefono: string;
  password: string;
  nombreUsuario: string;
  activo: boolean;
  imagenUrl: string | null;
  empresaId: number | null;
}

export interface UpdateUsuarioDto {
  telefono: string;
  password: string | null;
  nombreUsuario: string;
  activo: boolean;
  imagenUrl: string | null;
  empresaId: number | null;
}

class UsuariosService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async getAll(): Promise<Usuario[]> {
    return this.request<Usuario[]>('/usuarios');
  }

  async getById(id: number): Promise<Usuario> {
    return this.request<Usuario>(`/usuarios/${id}`);
  }

  async create(data: CreateUsuarioDto): Promise<Usuario> {
    return this.request<Usuario>('/usuarios', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async update(id: number, data: UpdateUsuarioDto): Promise<Usuario> {
    return this.request<Usuario>(`/usuarios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<void>(`/usuarios/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadImage(id: number, file: File): Promise<{ imagenUrl: string }> {
    const url = `${this.baseUrl}/usuarios/${id}/upload-imagen`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error al subir la imagen',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    return response.json();
  }
}

export const usuariosService = new UsuariosService();
